import _ from "lodash";
import {
    FormLayout,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Select,
    Switch,
    Text,
    Thumbnail,
} from "@/ui";
import { currencyMask } from "../../utils/maskInput";
import styles from "./GeneralsForm.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const toSelectOptions = (options = []) =>
    options
        .filter(option => option?.value != null && option.value !== "")
        .map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        }));

function ImageUrlField({ label, name, value, onChange }) {
    const url = value ?? "";

    return (
        <div className={styles.logoField}>
            <Input
                fullWidth
                iconLeft={
                    url ? (
                        <Thumbnail
                            alt=""
                            size="xs"
                            src={url}
                            variant="product-image"
                        />
                    ) : undefined
                }
                label={label}
                name={name}
                onChange={onChange}
                value={url}
            />
            {url ? (
                <Thumbnail
                    alt={`${label} preview`}
                    bordered
                    className={styles.logoPreview}
                    size="md"
                    src={url}
                    variant="product-image"
                />
            ) : null}
        </div>
    );
}

const GeneralsForm = ({
    toolbar,
    inputs,
    inputChange,
    selectChange,
    loading,
    timezonesOptions,
    currencyOptions,
    accountId,
    storeId,
    validator = {},
}) => {
    const addProtocol = e => {
        const value = e.target.value;

        e.target.value = _.trimEnd(
            !_.startsWith(value, "http") ? `https://${value}` : value,
            "/",
        );

        if (value !== e.target.value) {
            inputChange(e);
        }
    };

    const timezoneOptions = toSelectOptions(timezonesOptions);
    const currencySelectOptions = toSelectOptions(currencyOptions);

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>General information</Heading>
                        <Text tone="secondary" variant="body-md">
                            Business General Information
                        </Text>
                    </div>
                    {toolbar ? (
                        <div className={styles.headerActions}>{toolbar}</div>
                    ) : null}
                </div>
            }
            showHeaderDivider
        >
            {loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section title="Store information">
                        <Switch
                            checked={Boolean(inputs.status)}
                            label="Status"
                            name="status"
                            onChange={inputChange}
                        />
                        <FormLayout.Grid columns={3}>
                            <Input
                                error={fieldError(validator, "name")}
                                fullWidth
                                label="Store Name"
                                name="name"
                                onChange={inputChange}
                                required
                                value={inputs.name ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "domain")}
                                fullWidth
                                helperText="Please make sure to include the https protocol, E.g. https://example.com"
                                label="Domain"
                                name="domain"
                                onBlur={addProtocol}
                                onChange={inputChange}
                                required
                                value={inputs.domain ?? ""}
                            />
                        </FormLayout.Grid>
                        <FormLayout.Grid columns={3}>
                            <ImageUrlField
                                label="Large Logo"
                                name="logo"
                                onChange={inputChange}
                                value={inputs.logo}
                            />
                            <ImageUrlField
                                label="Small Logo"
                                name="logo_small"
                                onChange={inputChange}
                                value={inputs.logo_small}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="Store representative">
                        <FormLayout.Grid columns={3}>
                            <Input
                                error={fieldError(validator, "first_name")}
                                fullWidth
                                label="First Name"
                                name="first_name"
                                onChange={inputChange}
                                required
                                value={inputs.first_name ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "last_name")}
                                fullWidth
                                label="Last Name"
                                name="last_name"
                                onChange={inputChange}
                                required
                                value={inputs.last_name ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "email")}
                                fullWidth
                                label="Email"
                                name="email"
                                onChange={inputChange}
                                required
                                type="email"
                                value={inputs.email ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "phone_number")}
                                fullWidth
                                label="Phone"
                                name="phone_number"
                                onChange={inputChange}
                                required
                                value={inputs.phone_number ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "license")}
                                fullWidth
                                label="License"
                                name="license"
                                onChange={inputChange}
                                required
                                value={inputs.license ?? ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="Location">
                        <FormLayout.Grid columns={3}>
                            <Input
                                error={fieldError(validator, "address1")}
                                fullWidth
                                label="Address 1"
                                name="address1"
                                onChange={inputChange}
                                required
                                value={inputs.address1 ?? ""}
                            />
                            <Input
                                fullWidth
                                label="Address 2"
                                name="address2"
                                onChange={inputChange}
                                value={inputs.address2 ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "city")}
                                fullWidth
                                label="City"
                                name="city"
                                onChange={inputChange}
                                required
                                value={inputs.city ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "province_code")}
                                fullWidth
                                label="State Code"
                                name="province_code"
                                onChange={inputChange}
                                required
                                value={inputs.province_code ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "zip")}
                                fullWidth
                                label="Zip"
                                name="zip"
                                onChange={inputChange}
                                required
                                value={inputs.zip ?? ""}
                            />
                            <Input
                                error={fieldError(validator, "country_code")}
                                fullWidth
                                label="Country Code"
                                name="country_code"
                                onChange={inputChange}
                                required
                                value={inputs.country_code ?? ""}
                            />
                            <Input
                                fullWidth
                                label="Latitude"
                                name="latitude"
                                onChange={inputChange}
                                value={inputs.latitude ?? ""}
                            />
                            <Input
                                fullWidth
                                label="Longitude"
                                name="longitude"
                                onChange={inputChange}
                                value={inputs.longitude ?? ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="Timezone and currency">
                        <FormLayout.Grid columns={3}>
                            <Select
                                error={fieldError(validator, "timezone")}
                                fullWidth
                                label="Timezone"
                                onValueChange={value =>
                                    selectChange({ value }, "timezone")
                                }
                                options={timezoneOptions}
                                value={
                                    inputs.timezone
                                        ? String(inputs.timezone)
                                        : undefined
                                }
                            />
                            <Select
                                error={fieldError(validator, "currency")}
                                fullWidth
                                label="Currency"
                                onValueChange={value =>
                                    selectChange({ value }, "currency")
                                }
                                options={currencySelectOptions}
                                value={
                                    inputs.currency
                                        ? String(inputs.currency)
                                        : undefined
                                }
                            />
                            <Input
                                fullWidth
                                label="Money Format"
                                name="money_format"
                                onChange={inputChange}
                                value={inputs.money_format ?? ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="Minimum order">
                        <FormLayout.Grid columns={3}>
                            <Input
                                fullWidth
                                label="Amount"
                                mask={currencyMask(
                                    inputs.min_order_amount ?? "",
                                )}
                                name="min_order_amount"
                                onChange={inputChange}
                                prefix="$"
                                value={inputs.min_order_amount ?? ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>

                    <FormLayout.Section title="Account Info">
                        <FormLayout.Grid columns={3}>
                            <Input
                                fullWidth
                                label="Organization Name"
                                name="parent_organization"
                                onChange={inputChange}
                                value={inputs.parent_organization ?? ""}
                            />
                            <Input
                                disabled
                                fullWidth
                                label="Account ID"
                                name="account_id"
                                value={accountId ?? ""}
                            />
                            <Input
                                disabled
                                fullWidth
                                label="Store ID"
                                name="store_id"
                                value={storeId ?? ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default GeneralsForm;
